import { Fragment, useEffect, useState } from "react";
import { JournalEntryT, api } from "../api";

function fmtTime(s: string | null) {
  if (!s) return "—";
  return new Date(s).toLocaleString();
}

function StatusPill({ status }: { status: string | null }) {
  if (!status) return <span className="text-fg-muted">—</span>;
  const color = status === "GREEN" ? "text-success" : status === "AMBER" ? "text-warn" : "text-danger";
  return <span className={`uppercase font-bold ${color}`}>{status}</span>;
}

function RowExpanded({
  entry,
  onSave,
  onCancel,
}: {
  entry: JournalEntryT;
  onSave: (notes: string, tags: string, snapshotUrl: string) => void;
  onCancel: () => void;
}) {
  const [notes, setNotes] = useState(entry.notes);
  const [tags, setTags] = useState(entry.tags.join(", "));
  const [snapshotUrl, setSnapshotUrl] = useState(entry.entry_snapshot_url ?? "");
  const [busy, setBusy] = useState(false);

  return (
    <tr className="bg-page border-b border-border">
      <td colSpan={9} className="p-3">
        <div className="flex flex-col gap-2 text-sm">
          <div>
            <label className="block text-xs uppercase text-fg-muted mb-1">Notes</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className="w-full bg-surface border border-border rounded p-2 font-mono text-sm"
            />
          </div>
          <div>
            <label className="block text-xs uppercase text-fg-muted mb-1">
              Tags (comma-separated)
            </label>
            <input
              type="text"
              value={tags}
              onChange={(e) => setTags(e.target.value)}
              placeholder="fomo, breakout, setup-A"
              className="w-full bg-surface border border-border rounded p-2 font-mono text-sm"
            />
          </div>
          <div>
            <label className="block text-xs uppercase text-fg-muted mb-1">
              Chart snapshot URL
            </label>
            <input
              type="url"
              value={snapshotUrl}
              onChange={(e) => setSnapshotUrl(e.target.value)}
              placeholder="https://… (e.g. a TradingView snapshot link)"
              className="w-full bg-surface border border-border rounded p-2 font-mono text-sm"
            />
            {snapshotUrl.startsWith("http") && (
              <a href={snapshotUrl} target="_blank" rel="noreferrer">
                <img
                  src={snapshotUrl}
                  alt="entry chart snapshot"
                  className="mt-2 max-h-48 rounded border border-border"
                  onError={(ev) => {
                    (ev.target as HTMLImageElement).style.display = "none";
                  }}
                />
              </a>
            )}
          </div>
          {Object.entries(entry.entry_context ?? {}).map(([sid, ctx]) => (
            <div key={sid} className="text-xs font-mono">
              <span className="text-fg">{sid}</span>
              {ctx.reason && <span className="text-fg-muted"> — {ctx.reason}</span>}
              {ctx.indicators && (
                <div className="text-[10px] text-fg-muted">
                  {Object.entries(ctx.indicators)
                    .filter(([, v]) => v !== null && v !== undefined)
                    .map(([k, v]) => `${k} ${v}`)
                    .join(" · ")}
                </div>
              )}
            </div>
          ))}
          <div className="text-xs text-fg-muted font-mono">
            strategies: {entry.contributing_strategies.join(", ") || "—"} ·{" "}
            equity at entry: {entry.equity_at_entry?.toFixed(2) ?? "—"} ·{" "}
            mt5 ticket: {entry.mt5_ticket ?? "—"}
          </div>
          <div className="flex gap-2">
            <button
              onClick={async () => {
                setBusy(true);
                await onSave(notes, tags, snapshotUrl);
                setBusy(false);
              }}
              disabled={busy}
              className="px-3 py-1 rounded bg-success/20 border border-success text-success font-bold uppercase text-xs disabled:opacity-50"
            >
              {busy ? "saving…" : "save"}
            </button>
            <button
              onClick={onCancel}
              disabled={busy}
              className="px-3 py-1 rounded bg-surface-2 border border-border text-fg-muted text-xs disabled:opacity-50"
            >
              cancel
            </button>
          </div>
        </div>
      </td>
    </tr>
  );
}

export function JournalPanel() {
  const [entries, setEntries] = useState<JournalEntryT[]>([]);
  const [symbol, setSymbol] = useState("");
  const [tag, setTag] = useState("");
  const [search, setSearch] = useState("");
  const [editingId, setEditingId] = useState<number | null>(null);
  const [reloadNonce, setReloadNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const r = await api.journalList({
        symbol: symbol || undefined,
        tag: tag || undefined,
        search: search || undefined,
        limit: 50,
      });
      if (cancelled) return;
      if ("entries" in r) setEntries(r.entries);
      else setEntries([]);
    })();
    return () => {
      cancelled = true;
    };
  }, [symbol, tag, search, reloadNonce]);

  async function handleSave(
    id: number,
    notes: string,
    tags: string,
    snapshotUrl: string,
  ) {
    await api.journalPatch(id, { notes, tags, entry_snapshot_url: snapshotUrl });
    setEditingId(null);
    setReloadNonce((n) => n + 1);
  }

  return (
    <section className="bg-surface border border-border rounded p-4">
      <div className="flex items-baseline justify-between mb-3 gap-3 flex-wrap">
        <h2 className="text-sm uppercase tracking-wider text-fg-muted">
          Trade journal
        </h2>
        <div className="flex gap-2 items-center text-sm">
          <input
            type="text"
            value={symbol}
            onChange={(e) => setSymbol(e.target.value.toUpperCase())}
            placeholder="symbol"
            className="bg-page border border-border rounded px-2 py-1 w-24 font-mono text-xs"
          />
          <input
            type="text"
            value={tag}
            onChange={(e) => setTag(e.target.value.toLowerCase())}
            placeholder="tag"
            className="bg-page border border-border rounded px-2 py-1 w-24 font-mono text-xs"
          />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="search notes"
            className="bg-page border border-border rounded px-2 py-1 w-40 font-mono text-xs"
          />
          <a
            href={api.journalCsvUrl({
              symbol: symbol || undefined,
              tag: tag || undefined,
              search: search || undefined,
            })}
            className="px-3 py-1 rounded bg-surface-2 border border-border text-fg font-bold uppercase text-xs hover:bg-bg-hover"
          >
            export csv
          </a>
        </div>
      </div>

      {entries.length === 0 ? (
        <p className="text-fg-muted text-sm">
          no journal entries yet · auto-created on every filled trade
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm font-mono">
            <thead className="text-xs uppercase text-fg-muted">
              <tr className="border-b border-border">
                <th className="text-left py-2 pr-3">ID</th>
                <th className="text-left py-2 pr-3">Time</th>
                <th className="text-left py-2 pr-3">Symbol</th>
                <th className="text-left py-2 pr-3">Dir</th>
                <th className="text-right py-2 pr-3">Lot</th>
                <th className="text-right py-2 pr-3">Entry</th>
                <th className="text-left py-2 pr-3">Safe</th>
                <th className="text-left py-2 pr-3">Tags</th>
                <th className="text-left py-2 pr-3">Notes</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((e) => (
                <Fragment key={e.id}>
                  <tr
                    className="border-b border-border/60 cursor-pointer hover:bg-surface-2/40"
                    onClick={() =>
                      setEditingId((cur) => (cur === e.id ? null : e.id))
                    }
                  >
                    <td className="py-1 pr-3 text-fg-muted">{e.id}</td>
                    <td className="py-1 pr-3 text-fg-muted">
                      {fmtTime(e.entry_time ?? e.created_at)}
                    </td>
                    <td className="py-1 pr-3">{e.symbol}</td>
                    <td
                      className={`py-1 pr-3 uppercase ${e.direction === "buy" ? "text-success" : "text-danger"}`}
                    >
                      {e.direction}
                    </td>
                    <td className="py-1 pr-3 text-right">
                      {e.lot_size.toFixed(2)}
                    </td>
                    <td className="py-1 pr-3 text-right">
                      {e.entry_price !== null ? e.entry_price.toFixed(5) : "—"}
                    </td>
                    <td className="py-1 pr-3">
                      <StatusPill status={e.safe_to_trade_status} />
                    </td>
                    <td className="py-1 pr-3 text-fg-muted text-xs truncate max-w-xs">
                      {e.tags.length > 0 ? e.tags.join(", ") : <span className="text-fg-muted">—</span>}
                    </td>
                    <td className="py-1 pr-3 text-fg-muted text-xs truncate max-w-xs">
                      {e.notes || <span className="text-fg-muted">click to add</span>}
                    </td>
                  </tr>
                  {editingId === e.id && (
                    <RowExpanded
                      entry={e}
                      onSave={(notes, tags, snap) => handleSave(e.id, notes, tags, snap)}
                      onCancel={() => setEditingId(null)}
                    />
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
